import { Component, OnInit } from '@angular/core';
import { PagedResultDto, LocalizationService } from '@abp/ng.core';
import { Column } from '../shared/components/metro-table/metro-table.component';
import { CommunicationDto, GetCommunicationsInput } from '../proxy/communications/models';
import { CommunicationService } from '../proxy/communications/communication.service';
import { ConfirmationModalService } from '../shared/services/confirmation-modal.service';
import { Router } from '@angular/router';
import { MetroToastService } from '../shared/services/metro-toast.service';
import { HostListener } from '@angular/core';

@Component({
  selector: 'app-communications',
  templateUrl: './communications.component.html',
  standalone: false,
})
export class CommunicationsComponent implements OnInit {
  columns: Column[] = [
    { name: 'message', label: '::Message', width: '50%' },
    { name: 'creationTimeDisplay', label: '::CreationTime', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'statusDisplay', label: '::Status', width: '15%' },
  ];

  actions: any[] = [
    { name: 'edit', label: '::Edit', handler: (row: CommunicationDto) => this.openEditModal(row) },
    { 
      name: 'toggleActive', 
      label: (row: CommunicationDto) => row.isActive ? '::Deactivate' : '::Activate', 
      handler: (row: CommunicationDto) => this.toggleActive(row) 
    },
    { name: 'delete', label: '::Delete', handler: (row: CommunicationDto) => this.deleteCommunication(row) }
  ];

  data: PagedResultDto<CommunicationDto> = { items: [], totalCount: 0 };
  searchFilter = {} as GetCommunicationsInput;
  
  isLoading = true;
  currentPage = 1;
  maxResultCount = 3;

  constructor(
    private communicationService: CommunicationService,
    private confirmation: ConfirmationModalService,
    private router: Router,
    private localizationService: LocalizationService,
    private toast: MetroToastService
  ) {}

  ngOnInit(): void {
    this.updatePageSize();
    this.loadCommunications();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const oldSize = this.maxResultCount;
    this.updatePageSize();
    if (oldSize !== this.maxResultCount) {
      this.currentPage = 1;
      this.loadCommunications();
    }
  }

  private updatePageSize() {
    const isMobile = window.innerWidth < 768;
    this.maxResultCount = isMobile ? 3 : 10;
  }

  loadCommunications() {
    this.isLoading = true;
    
    if (isNaN(this.currentPage) || this.currentPage < 1) {
      this.currentPage = 1;
    }

    const skipCount = (this.currentPage - 1) * this.maxResultCount;

    const params: GetCommunicationsInput = {
      filter: this.searchFilter.filter ?? undefined,
      skipCount,
      maxResultCount: this.maxResultCount,
      sorting: 'creationTime desc'
    };

    this.communicationService.getList(params).subscribe({
      next: (result) => {
        this.data = {
          ...result,
          items: (result.items || []).map(item => ({
            ...item,
            creationTimeDisplay: item.creationTime ? new Date(item.creationTime).toLocaleDateString('es-AR', { day:'numeric', month:'numeric', year:'numeric', hour: '2-digit', minute: '2-digit' }) : '---',
            statusDisplay: item.isActive ? this.localizationService.instant('::IsActive') : this.localizationService.instant('::Inactive')
          }))
        };
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error cargando comunicaciones:', error);
        this.isLoading = false;
      }
    });
  }

  getTotalPages(): number {
    return Math.ceil(this.data.totalCount / this.maxResultCount);
  }

  getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const pages: number[] = [];
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  onSearch(searchTerm: string) {
    this.searchFilter.filter = searchTerm;
    this.currentPage = 1;
    this.loadCommunications();
  }

  onClearSearch() {
    this.searchFilter.filter = undefined;
    this.currentPage = 1;
    this.loadCommunications();
  }

  openCreateModal() {
    this.router.navigate(['/communications/create']);
  }

  openEditModal(communication: CommunicationDto) {
    this.router.navigate(['/communications/edit', communication.id]);
  }

  toggleActive(communication: CommunicationDto) {
    const action = communication.isActive ? '::Deactivate' : '::Activate';
    this.confirmation.confirm({
      title: this.localizationService.instant(action),
      message: '::AreYouSure',
      type: 'warning',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.communicationService.update(communication.id, {
        message: communication.message,
        isActive: !communication.isActive
      }).subscribe({
        next: () => {
          this.toast.success('::SavedSuccessfully', '::Success');
          this.loadCommunications();
        },
        error: (err) => {
          this.toast.error('Ocurrió un error al actualizar el estado.', '::Error');
          console.error(err);
        }
      });
    });
  }

  deleteCommunication(communication: CommunicationDto) {
    this.confirmation.confirm({
      title: this.localizationService.instant('::Delete'),
      message: '::AreYouSureToDelete',
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.communicationService.delete(communication.id).subscribe({
        next: () => {
          this.toast.success('::SuccessfullyRemoved', '::Success');
          this.loadCommunications();
        },
        error: (err) => {
          this.toast.error('Ocurrió un error al eliminar.', '::Error');
          console.error(err);
        }
      });
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadCommunications();
  }
}
